import React, { useState, useEffect, useContext } from 'react';
import { Box, Stack, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, CircularProgress } from '@mui/material';
import { Table, TableBody, TableContainer, TableHead, TableRow, Typography, Paper } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { HomeContext } from '../utils/contexts/homeContext';
import { NurseFormContext } from '../utils/contexts/nurseFormContext';
import {
    GetDayJSValueFromUTCDate, GetDateFromDayJSValue, GetDateOnlyStringValue, GetNoToCurrencyFormat, GetNumberFromCurrencyFormat,
    NurseAdvanceReturnPaySearchAPIUrl, NurseAdvanceReturnPayAPIUrl, NurseAdvancePaySearchAPIUrl
} from '../utils/extensions/functionEx';
import StyledTableRow from '../shared/components/styledTableRow';
import StyledTableCell from '../shared/components/styledTableCell';
import dayjs from 'dayjs';
import { useCookies } from 'react-cookie';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

export default function NurseAdvancePayReturnForm(props) {
    const { isOpen, nurseId, handleClose, selectedPayment, selectedAdvReturnPayment } = props;

    const { ShowAPIError, OpenErrorAlert, OpenSuccessAlert, homeState } = useContext(HomeContext);
    const { SetAdvanceReturnPayments, SetAdvancePayments, ClearLoggedUser } = useContext(NurseFormContext);

    const [cleared, setCleared] = useState(false);
    const [payDate, setPayDate] = useState(dayjs());
    const [amount, setAmount] = useState('0.00');
    const [notes, setNotes] = useState('');
    const [isSaveInProgress, setIsSaveInProgress] = useState(false);
    const [totalReturnedAmount, setTotalReturnedAmount] = useState('0.00');
    const [balanceAmount, setBalanceAmount] = useState('0.00');

    const [cookies, setCookie] = useCookies(['hplus_home_care_user_session_code']);

    useEffect(() => {
        if (isOpen) {
            let tempTotalRetAmount = 0;
            let tempBalAmount = 0;

            if (selectedPayment && selectedPayment.id > 0) {
                tempTotalRetAmount = selectedPayment.total_returned_amount;
                tempBalAmount = selectedPayment.balance_amount;
            }
            if (selectedAdvReturnPayment && selectedAdvReturnPayment.id > 0) {
                setPayDate(GetDayJSValueFromUTCDate(selectedAdvReturnPayment.pay_date));
                setAmount(GetNoToCurrencyFormat(parseFloat(selectedAdvReturnPayment.amount)));
                setNotes(selectedAdvReturnPayment.remarks);

                tempTotalRetAmount -= parseFloat(selectedAdvReturnPayment.amount);
                tempBalAmount += parseFloat(selectedAdvReturnPayment.amount);
            }
            setTotalReturnedAmount(GetNoToCurrencyFormat(tempTotalRetAmount));
            setBalanceAmount(GetNoToCurrencyFormat(tempBalAmount));
        }
    }, [isOpen, selectedPayment, selectedAdvReturnPayment]);

    useEffect(() => {
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);

            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [cleared]);

    const handleSaveClick = async () => {
        try {
            if (!payDate || !amount || amount.trim().length === 0) {
                OpenErrorAlert('Enter valid advance details.')
                return;
            }
            const tempAmount = GetNumberFromCurrencyFormat(amount);
            if (tempAmount <= 0) {
                OpenErrorAlert('Enter valid advance return amount.')
                return;
            }

            const tempBalanceAmt = GetNumberFromCurrencyFormat(balanceAmount);
            if (tempAmount > tempBalanceAmt) {
                OpenErrorAlert('Enter valid advance return amount.')
                return;
            }

            setIsSaveInProgress(true);

            const tempPayment = {
                id: 0,
                nurse_id: nurseId,
                pay_date: GetDateFromDayJSValue(payDate),
                amount: tempAmount,
                remarks: notes.trim(),
                advance_pay_returns: []
            };
            tempPayment.advance_pay_returns.push({
                id: 0,
                nurse_advance_return_id: 0,
                nurse_advance_payment_id: selectedPayment.id,
                amount: tempAmount
            });

            const headers = new Headers();
            headers.append("Accept", "application/json");
            headers.append("Accept-Encoding", "application/json");
            headers.append("Content-Type", "application/json");
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);

            let response;

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (selectedAdvReturnPayment && selectedAdvReturnPayment.id > 0) {
                tempPayment.id = selectedAdvReturnPayment.id;

                response = await fetch(NurseAdvanceReturnPayAPIUrl(homeState.loggedUser.companyCode), {
                    method: "PUT",
                    headers: headers,
                    body: JSON.stringify(tempPayment)
                });
            } else {
                response = await fetch(NurseAdvanceReturnPayAPIUrl(homeState.loggedUser.companyCode), {
                    method: "POST",
                    headers: headers,
                    body: JSON.stringify(tempPayment)
                });
            }

            if (response.ok) {
                OpenSuccessAlert('Nurse advance return payment saved successfully.');
                await fetchNurseAdvanceReturnPayments();
                await fetchNurseAdvancePayments();
                handleClose();

                return;
            }
            ShowAPIError(response);
        }
        catch (error) {
            OpenErrorAlert(error)
        }
        finally {
            setIsSaveInProgress(false);
        }
    }

    async function fetchNurseAdvanceReturnPayments() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseAdvanceReturnPaySearchAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers: headers });

            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetAdvanceReturnPayments(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }

    async function fetchNurseAdvancePayments() {
        try {
            const headers = new Headers();
            headers.append("Hc-App-Auth-Token", cookies.hplus_home_care_user_session_code);
            const response = await fetch(NurseAdvancePaySearchAPIUrl(homeState.loggedUser.companyCode, nurseId), { headers: headers });
            if (response.status === 401) {
                OpenErrorAlert('User login history expired, please log in again.');
                setCookie('hplus_home_care_user_session_code', '', { path: '/' });
                ClearLoggedUser();
            } else if (response.status === 200) {
                SetAdvancePayments(await response.json());
            } else {
                ShowAPIError(response);
            }
        }
        catch (error) {
            OpenErrorAlert(error);
        }
    }


    return (
        <React.Fragment>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"en-in"}>
                <BootstrapDialog
                    onClose={handleClose}
                    open={isOpen}
                    fullWidth={true}
                    maxWidth={"sm"}
                >
                    <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title-nurse-salary-payment-form">
                        Nurse Advance Return Form
                    </DialogTitle>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        sx={(theme) => ({
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: theme.palette.grey[500],
                        })}
                    >
                        <CloseIcon />
                    </IconButton>
                    <DialogContent dividers>
                        <Stack>
                            <Box sx={{ paddingTop: '4px' }}>
                                <Stack spacing={2}>
                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <Typography variant="subtitle2" sx={{ paddingTop: '8px' }}>
                                                Payment Returns Details
                                            </Typography>
                                        </Grid>
                                        <Grid size={6}>
                                            <DatePicker format="DD/MM/YYYY"
                                                label="Return Date" readOnly={false}
                                                slotProps={{
                                                    textField: {
                                                        size: "small", fullWidth: true, required: true,
                                                        error: !payDate ? true : false,
                                                        helperText: !payDate ? 'Enter payment date' : ''
                                                    },
                                                    field: {
                                                        clearable: true,
                                                        onClear: () => setCleared(true)
                                                    }
                                                }}
                                                value={payDate} minDate={dayjs().add(-3, 'day')} maxDate={dayjs()}
                                                onChange={(newValue) => setPayDate(newValue)}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <TextField size="small" fullWidth
                                                label="Return Amount"
                                                value={amount}
                                                error={!amount || amount.trim().length === 0 || amount.trim() === '0' || amount.trim() === '0.00'}
                                                helperText={!amount || amount.trim().length === 0 || amount.trim() === '0' || amount.trim() === '0.00' ? 'Invalid amount' : ''}
                                                onChange={(event) => {
                                                    setAmount(event.target.value);
                                                }}
                                                onBlur={(event) => {
                                                    const tempFormValue = GetNoToCurrencyFormat(event.target.value ?? 0);
                                                    setAmount(tempFormValue);
                                                }}
                                                onFocus={(event) => {
                                                    if ((event.target.value ?? 0) > 0) {
                                                        const tempValue = parseFloat(event.target.value ?? 0);
                                                        if (tempValue > 0) {
                                                            setAmount(GetNumberFromCurrencyFormat(event.target.value));
                                                        } else {
                                                            setAmount('0');
                                                        }
                                                    }
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextField size="small" fullWidth
                                                label="Remarks"
                                                value={notes}
                                                onChange={(event) => {
                                                    setNotes(event.target.value);
                                                }}
                                                slotProps={{
                                                    input: {
                                                        readOnly: false
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Grid container spacing={2}>
                                        <Grid size={12}>
                                            <Typography variant="subtitle2">
                                                Payment Details
                                            </Typography>
                                        </Grid>
                                        <Grid size={6}>
                                            <DatePicker format="DD/MM/YYYY"
                                                label="Payment Date" readOnly={true}
                                                slotProps={{
                                                    textField: {
                                                        size: "small", fullWidth: true
                                                    },
                                                    field: {
                                                        clearable: true,
                                                        onClear: () => setCleared(true)
                                                    }
                                                }}
                                                value={GetDayJSValueFromUTCDate(selectedPayment.pay_date)}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <TextField size="small" fullWidth
                                                label="Amount"
                                                value={GetNoToCurrencyFormat(parseFloat(selectedPayment.amount))}
                                                slotProps={{
                                                    input: {
                                                        readOnly: true
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <TextField size="small" fullWidth
                                                label="Returned Amount"
                                                value={totalReturnedAmount}
                                                slotProps={{
                                                    input: {
                                                        readOnly: true
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={6}>
                                            <TextField size="small" fullWidth
                                                label="Balance Amount"
                                                value={balanceAmount}
                                                slotProps={{
                                                    input: {
                                                        readOnly: true
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid size={12}>
                                            <TextField size="small" fullWidth
                                                label="Notes"
                                                value={selectedPayment.remarks}
                                                slotProps={{
                                                    input: {
                                                        readOnly: true
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    {
                                        selectedPayment && selectedPayment.id && selectedPayment.id > 0 &&
                                        <Grid container spacing={1}>
                                            <Grid size={12}>
                                                <Typography variant="subtitle2" sx={{ paddingTop: '8px' }}>
                                                    Payment Returns History
                                                </Typography>
                                            </Grid>
                                            <Grid size={12}>

                                                <TableContainer component={Paper} sx={{ maxHeight: '77vh', borderRadius: '0' }}>
                                                    <Table stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <StyledTableCell sx={{ width: '250px' }}>Returned Date</StyledTableCell>
                                                                <StyledTableCell sx={{ width: '250px' }}>Returned Amount</StyledTableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {selectedPayment.return_pay_details &&
                                                                selectedPayment.return_pay_details
                                                                    .filter(col => !selectedAdvReturnPayment || !selectedAdvReturnPayment.id || selectedAdvReturnPayment.id === 0
                                                                        || col.nurse_advance_return_id !== selectedAdvReturnPayment.id)
                                                                    .map((row) => (
                                                                        <StyledTableRow>
                                                                            <StyledTableCell>{GetDateOnlyStringValue(GetDayJSValueFromUTCDate(row.pay_date))}</StyledTableCell>
                                                                            <StyledTableCell>{GetNoToCurrencyFormat(row.amount)}</StyledTableCell>
                                                                        </StyledTableRow>
                                                                    ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </Grid>
                                        </Grid>
                                    }

                                </Stack>
                            </Box>
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        {
                            isSaveInProgress && <CircularProgress size="30px" />
                        }
                        <Button variant="contained" onClick={handleSaveClick} disabled={selectedPayment && selectedPayment.balance_amount <= 0 ? true : false}>
                            Save
                        </Button>
                    </DialogActions>
                </BootstrapDialog> </LocalizationProvider>
        </React.Fragment>

    );
}
